import { useCallback, useEffect, useState } from 'react';
import styles from './GameTable.module.css';
import { getPage } from '../../helpers/api';
import { firstChineseChar } from '../../helpers/firstChineseChar';

type GameActivationStat = {
  gameId: string;
  gameName: string;
  count: number;
};

const PAGE_SIZE = 5;
const GAME_COLORS = ['blue', 'purple', 'orange', 'red', 'cyan'];

const GamepadIcon = () => (
  <svg viewBox="0 0 24 24">
    <path d="M7 8h10a5 5 0 0 1 4.6 7.1l-1.1 2.4a2 2 0 0 1-3.5.2l-1.7-2.4H8.7L7 17.7a2 2 0 0 1-3.5-.2l-1.1-2.4A5 5 0 0 1 7 8z" />
    <path d="M8 12v4" />
    <path d="M6 14h4" />
    <circle cx="16" cy="13" r="1" />
    <circle cx="18.5" cy="15" r="1" />
  </svg>
);

export const GameTable = () => {
  const [games, setGames] = useState<GameActivationStat[]>([]);
  const [page, setPage] = useState(1);
  const [pages, setPages] = useState(0);
  const [loading, setLoading] = useState(false);
  const [loadingDir, setLoadingDir] = useState<'prev' | 'next' | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [refreshKey, setRefreshKey] = useState(0);
  const [spinning, setSpinning] = useState(false);

  const fetchGames = useCallback(async (pageNum: number, dir: 'prev' | 'next' | null = null) => {
    setLoading(true);
    setLoadingDir(dir);
    setError(null);
    try {
      const result = await getPage<GameActivationStat>(
        `/admin/tel/game/stats?page=${pageNum}&limit=${PAGE_SIZE}`
      );
      setGames(result.records);
      setPage(result.current);
      setPages(result.pages);
    } catch (e) {
      setError(e instanceof Error ? e.message : '加载失败');
    } finally {
      setLoading(false);
      setLoadingDir(null);
    }
  }, []);

  useEffect(() => {
    fetchGames(1);
  }, [fetchGames, refreshKey]);

  const handleRefresh = () => {
    setSpinning(true);
    setRefreshKey((key) => key + 1);
    setTimeout(() => setSpinning(false), 600);
  };

  const total = games.reduce((sum, game) => sum + (Number(game.count) || 0), 0);
  const placeholders = Array.from({ length: Math.max(0, PAGE_SIZE - games.length) });

  return (
    <div className="card">
      <div className="card-header">
        <div className="section-title">
          <div className="icon-box">
            <GamepadIcon />
          </div>
          今日游戏激活
        </div>
        <div className="card-header-right">
          <button type="button" className="card-refresh" onClick={handleRefresh}>
            <svg
              className={spinning ? 'card-refresh-spin' : ''}
              viewBox="0 0 24 24"
              fill="none"
              stroke="currentColor"
              strokeWidth="2"
              strokeLinecap="round"
              strokeLinejoin="round"
              aria-hidden="true"
            >
              <path d="M20 11a8 8 0 0 0-14-5L3 8" />
              <path d="M3 4v4h4" />
              <path d="M4 13a8 8 0 0 0 14 5l3-2" />
              <path d="M21 20v-4h-4" />
            </svg>
          </button>
          <div className="section-tag">游戏统计</div>
        </div>
      </div>
      <div className={`${styles.table} ${loading ? styles.loading : ''}`}>
        <div className={styles.tableHead}>
          <span>游戏</span>
          <span className={styles.center}>激活数</span>
          <span className={styles.right}>占比</span>
        </div>
        {games.map((game, index) => (
          <div key={game.gameId} className={styles.row}>
            <span className={styles.nameCell}>
              <span className={`game-icon ${GAME_COLORS[index % GAME_COLORS.length]}`}>
                <span className={styles.letter}>
                  {firstChineseChar(game.gameName || game.gameId || '')}
                </span>
              </span>
              <span className={styles.name}>{game.gameName || game.gameId}</span>
            </span>
            <span className={`${styles.count} ${styles.center}`}>
              {Number(game.count || 0).toLocaleString()}
            </span>
            <span className={`${styles.percentage} ${styles.right} ${index === 0 ? styles.top : ''}`}>
              {total > 0 ? `${Math.round(Number(game.count) / total * 100)}%` : '-'}
            </span>
          </div>
        ))}
        {placeholders.map((_, index) => (
          <div key={`empty-${index}`} className={styles.row} />
        ))}
      </div>
      {error && <div className="error">{error}</div>}
      {pages > 1 && (
        <div className="pagination">
          <button
            type="button"
            className="page-btn prev"
            disabled={loading || page <= 1}
            onClick={() => fetchGames(page - 1, 'prev')}
          >
            {loading && loadingDir === 'prev' ? <span className="spinner" /> : <span>&lt;</span>}
            上一页
          </button>
          <span className="page-number">{page} / {pages}</span>
          <button
            type="button"
            className="page-btn next"
            disabled={loading || page >= pages}
            onClick={() => fetchGames(page + 1, 'next')}
          >
            下一页
            {loading && loadingDir === 'next' ? <span className="spinner" /> : <span>&gt;</span>}
          </button>
        </div>
      )}
    </div>
  );
};